import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BellIcon,
  UserGroupIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import '../../styles/components.css';
import '../../styles/dark-mode.css';

const AdminFunctions = () => {
  const [emailStatus, setEmailStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmailStatus();
  }, []);

  const fetchEmailStatus = async () => {
    try {
      const response = await axios.get('/api/admin/email-service-status');
      
      if (response.status === 200) {
        const data = response.data;
        setEmailStatus(data);
      }
    } catch (error) {
      console.error('Error fetching email status:', error);
    } finally {
      setLoading(false);
    }
  };

  const testEmail = async () => {
    try {
      const response = await axios.post('/api/admin/test-email', {
        to: 'info@squaredot.nz',
        subject: 'FitFlow System Test',
        message: 'This is a test email to verify the email system is working correctly.'
      });

      if (response.status === 200 || response.status === 201) {
        alert('Test email sent successfully!');
      } else {
        alert('Failed to send test email. Check console for details.');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Error sending test email. Check console for details.');
    }
  };

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <h2 className="text-lg font-semibold text-primary mb-2">
            System Functions & Documentation
          </h2>
          <p className="text-secondary">
            Comprehensive guide to how notifications, waitlists, and email systems work in FitFlow
          </p>
        </div>

        {/* Email System Status */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-primary flex items-center">
              <EnvelopeIcon className="h-5 w-5 mr-2" />
              Email System Status
            </h2>
            <button
              onClick={testEmail}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Send Test Email
            </button>
          </div>
          
          {loading ? (
            <div className="flex items-center text-secondary">
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
              Loading email status...
            </div>
          ) : emailStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-secondary mr-2">Status:</span>
                  <span className={`text-sm font-medium ${
                    emailStatus.health?.status === 'healthy' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {emailStatus.health?.status || 'Unknown'}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-secondary mr-2">Provider:</span>
                  <span className="text-sm text-primary">{emailStatus.status?.provider || 'Unknown'}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-secondary mr-2">From Email:</span>
                  <span className="text-sm text-primary">{emailStatus.status?.fromEmail || 'Not configured'}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-secondary mr-2">Environment:</span>
                  <span className="text-sm text-primary">{emailStatus.status?.environment || 'Unknown'}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-secondary mr-2">Configured:</span>
                  <span className={`text-sm font-medium ${
                    emailStatus.status?.configured ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {emailStatus.status?.configured ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-secondary mr-2">Test Mode:</span>
                  <span className={`text-sm font-medium ${
                    emailStatus.status?.testMode ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {emailStatus.status?.testMode ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-red-600 text-sm">
              Unable to fetch email status. Check system configuration.
            </div>
          )}
        </div>

        {/* Notification System */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <h2 className="text-lg font-semibold text-primary mb-4 flex items-center">
            <BellIcon className="h-5 w-5 mr-2" />
            Notification System
          </h2>
          
          <div className="space-y-6">
            {/* User Notifications */}
            <div>
              <h3 className="text-md font-medium text-primary mb-3">User Notifications</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-primary">Booking Confirmations</p>
                    <p className="text-sm text-secondary">
                      Users receive both in-app and email notifications when they successfully book a class or when an admin manually adds them to a class.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-primary">Class Cancellations</p>
                    <p className="text-sm text-secondary">
                      Users are notified when classes are cancelled, including information about concession refunds.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-primary">Waitlist Notifications</p>
                    <p className="text-sm text-secondary">
                      Users receive notifications when spots become available in classes they're waitlisted for.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-primary">Low Concession Alerts</p>
                    <p className="text-sm text-secondary">
                      Users receive notifications when they have 0 concessions or reach the -2 limit.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-primary">Concession Additions</p>
                    <p className="text-sm text-secondary">
                      Users are notified when concessions are added to their account by an admin.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Notifications */}
            <div>
              <h3 className="text-md font-medium text-primary mb-3">Admin Notifications</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-primary">User Low Concessions</p>
                    <p className="text-sm text-secondary">
                      Admins are notified when users have low concession counts and may need more added.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-primary">Contact Details Changes</p>
                    <p className="text-sm text-secondary">
                      Admins are notified when users update their contact information.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-primary">Waitlist Activity</p>
                    <p className="text-sm text-secondary">
                      Admins can monitor waitlist activity and manage class availability.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Waitlist System */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <h2 className="text-lg font-semibold text-primary mb-4 flex items-center">
            <UserGroupIcon className="h-5 w-5 mr-2" />
            Waitlist System
          </h2>
          
          <div className="space-y-4">
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <InformationCircleIcon className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-primary-900 dark:text-primary-100">How Waitlists Work</h4>
                  <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                    When a class reaches full capacity, users can join a waitlist. The system maintains the order and sends notifications when spots become available.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <BellIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-primary">First-Come, First-Served</p>
                  <p className="text-sm text-secondary">
                    Waitlist positions are determined by when users joined. The system maintains the order automatically.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-primary">Email Notifications</p>
                  <p className="text-sm text-secondary">
                    Users receive email notifications when spots become available, with a link to book the class.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-primary">Availability</p>
                  <p className="text-sm text-secondary">
                    When spots become available, they are offered to all users on the waitlist. There is no time limit - it's purely first-come, first-served.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email System Details */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <h2 className="text-lg font-semibold text-primary mb-4 flex items-center">
            <EnvelopeIcon className="h-5 w-5 mr-2" />
            Email System Details
          </h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-primary mb-2">Professional Branding</h4>
                <p className="text-sm text-secondary">
                  All emails are sent from <strong>noreply@younitefitness.co.nz</strong> with "Younite Fitness" as the sender name.
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-primary mb-2">Reliable Delivery</h4>
                <p className="text-sm text-secondary">
                  Uses Mailgun service with automatic fallbacks, retry logic, and rate limiting for reliable email delivery.
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-primary mb-2">Environment Aware</h4>
                <p className="text-sm text-secondary">
                  Different configurations for development, staging, and production environments with appropriate settings.
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-primary mb-2">Monitoring</h4>
                <p className="text-sm text-secondary">
                  Built-in health checks, delivery tracking, and admin endpoints for monitoring email system status.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* System Behavior */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <h2 className="text-lg font-semibold text-primary mb-4">System Behavior & Rules</h2>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-primary mb-2">24-Hour Cancellation Policy</h4>
              <p className="text-sm text-secondary">
                Users can cancel bookings outside 24 hours for full concession refund. Cancellations within 24 hours do not receive refunds.
              </p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-primary mb-2">Concession Limits</h4>
              <p className="text-sm text-secondary">
                Users can book up to 2 classes in advance. The system enforces this limit to ensure fair access to classes.
              </p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-primary mb-2">Automatic Refunds</h4>
              <p className="text-sm text-secondary">
                Concessions are automatically refunded for valid cancellations and when classes are cancelled by admins.
              </p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-primary mb-2">Race Condition Prevention</h4>
              <p className="text-sm text-secondary">
                The system uses real-time data updates and proper locking mechanisms to prevent booking conflicts.
              </p>
            </div>
          </div>
        </div>
      </div>
  );
};

export default AdminFunctions;
